<script lang="ts" setup>
import GICS from 'gics'
import type { CompanyItem } from '~/models/Company'
import { useUser } from '~/store/user'

interface Props {
  item?: CompanyItem
  lite?: boolean
  animation?: boolean
  showVacancy?: boolean
  impressionSource: string
}

const props = withDefaults(defineProps<Props>(), {
  item: undefined,
  lite: false,
  animation: false,
  showVacancy: false,
  impressionSource: null,
})

const config = useRuntimeConfig()
const user = useUser()
const companyCard = ref(null)
const targetIsVisible = useElementVisibility(companyCard)
const wasVisibleOnce = ref(false)
const token = useCookie('token', { path: '/', domain: config.public.COOKIE_DOMAIN })
const timeoutId = ref<NodeJS.Timeout | null>(null)

watch(targetIsVisible, (isVisible) => {
  if (isVisible && !wasVisibleOnce.value) {
    timeoutId.value = setTimeout(() => {
      wasVisibleOnce.value = true
      impressionSend(token.value)
    }, 1000)
  }
  else if (!isVisible && timeoutId.value) {
    clearTimeout(timeoutId.value)
    timeoutId.value = null
  }
})

async function impressionSend(tokenValue: string) {
  try {
    const response = await useImpressionApi('/event', {
      method: 'PUT',
      body: {
        event: 'Company Impression',
        eventType: 'action',
        eventAction: 'company-impression',
        eventCategory: 'impression',
        platform: 'epicareer',
        data: {
          companyID: typeof props.item.id === 'string' ? Number(props.item.id.replace(/\D/g, '')) : props.item.id ?? 123456789,
          brandID: null,
          companyServiceID: 'ID',
          companyName: props.item.name ?? null,
          companySlug: props.item.slug ?? null,
          companyIndustry: Number(new GICS().findChild(props.item.industry).code ?? '000000'),
          userID: user.parsed.id ?? null,
          loginStatus: !isEmpty(tokenValue) ? 'logged_in' : 'non_logged_in',
          source: 'web',
          section: props.impressionSource ?? null,
          sessionID: tokenValue ?? null,
          platformID: 23,
          devicePlatform: 'web',
          deviceType: 'web',
          fingerprint: 'fingerprint',
        },
      },
    })

    return response
  }
  catch (error) {
    console.error('Error sending impression', error)
  }
}
</script>

<template>
  <div ref="companyCard">
    <div v-if="props.animation" class="h-full">
      <div v-if="lite" class="mr-6 flex items-center shadow border border-gray-100 rounded-md py-6 px-4 sm:px-6 bg-white">
        <div class="flex-shrink-0 mr-6">
          <div class="w-14 h-14 bg-gray-200 rounded-lg" />
        </div>
        <div class="w-full">
          <div class="w-[80%] bg-gray-200 h-4 mb-1 rounded-md" />
          <div class="w-[60%] bg-gray-200 h-3 rounded-md" />
          <div v-if="props.showVacancy" class="w-[55%] bg-gray-200 h-3 mt-1 rounded-md" />
        </div>
      </div>
      <div v-else class="flex-none shadow border border-gray-100 rounded-md py-6 px-4 sm:px-6 bg-white">
        <div class="w-14 h-14 mb-4 bg-gray-200 rounded-lg" />
        <div class="w-full">
          <div class="w-[35%] bg-gray-200 h-4 mb-1 rounded-md" />
          <div class="w-[100%] bg-gray-200 h-3 rounded-md" />
        </div>
      </div>
    </div>
    <div v-else-if="!props.animation && !isEmpty(props.item)" class="h-full">
      <NuxtLink :to="`/companies/${props.item.slug}`" class="relative">
        <div :class="`${lite ? 'flex items-center' : 'flex items-center sm:block'} h-full shadow border border-gray-100 rounded-md py-6 px-4 sm:px-6 bg-white cursor-pointer`">
          <div :class="`${lite ? 'mr-6' : 'mr-6 sm:mb-4'} flex-none`">
            <img v-if="props.item.logo" :src="props.item.logo" class="object-contain w-14 h-14">
            <div v-else class="bg-[#ECE9FC] text-[#5843E5] text-3xl font-bold text-center flex items-center w-14 h-14 rounded-lg">
              <span class="mx-auto">{{ props.item.name ? props.item.name.charAt(0) : '' }}</span>
            </div>
          </div>
          <div>
            <p class="font-bold mb-1 line-clamp-1">
              {{ props.item.name }}
            </p>
            <p class="text-sm text-gray-600 line-clamp-1">
              {{ props.item.industry || 'N/A' }}
            </p>
            <NuxtLink v-if="props.showVacancy" :to="`/companies/${props.item.slug}${props.item?.employerJobsCount > 0 ? '/jobs-vacancy' : ''}`" class="text-xs font-semibold text-[#5843e5]">
              {{ props.item?.employerJobsCount > 0 ? `${props.item?.employerJobsCount} Vacancy available` : 'See company' }}
            </NuxtLink>
          </div>
        </div>
      </NuxtLink>
    </div>
  </div>
</template>
